import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TanAuthentication, continueLaterLocalization } from "@vwfs-its/sf-sac-frontend";
import { Spinner } from "@vwfs-bronson/bronson-react";
import { continueJourney, sendSmsWithTan } from "../../services/ContinueJourneyService";
import TransactionService from "../../services/TransactionService";
import { useTranslation } from "react-i18next";

export const TanAuthenticationWrapper = () => {
    const { i18n } = useTranslation("sac");

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = React.useState(false);
    const [showTanAuthentication, setShowTanAuthentication] = React.useState(true);
    const [phoneData, setPhoneData] = React.useState<{ phoneNumber: string; phoneCountryCode: string } | null>(null);
    const [isPhoneDataLoaded, setIsPhoneDataLoaded] = React.useState(false);
    const smsRequestInProgressRef = React.useRef(false);

    // Support both 'transactionId' and 'cid' query parameters
    const transactionId = searchParams.get("transactionId") || searchParams.get("cid");
    const brand = process.env.REACT_APP_WEBSITE_BRAND;

    const productName = React.useMemo(() => {
        const productNames: Record<string, string> = {
            audi: "noleggio a lungo termine Audi Value Noleggio",
            vw: "noleggio a lungo termine SolutiON",
            seat: "noleggio a lungo termine SolutiON",
            cupra: "noleggio a lungo termine SolutiON",
            skoda: "noleggio a lungo termine SolutiON",
            vwn: "noleggio a lungo termine SolutiON",
        };
        return productNames[brand || "vw"] || "noleggio a lungo termine";
    }, [brand]);

    // Fetch transaction data to get phone number
    React.useEffect(() => {
        const fetchTransactionData = async () => {
            if (!transactionId) return;

            try {
                const transactionService = new TransactionService();
                const response = await transactionService.getTransaction(transactionId);

                const saveAndContinueInfo = response?.data?.data?.custom?.SaveAndContinueInfo;

                if (saveAndContinueInfo) {
                    setPhoneData({
                        phoneNumber: saveAndContinueInfo.continueLaterPhone || saveAndContinueInfo.phoneNumber || "",
                        phoneCountryCode: saveAndContinueInfo.continueLaterPhoneCountryCode || saveAndContinueInfo.phoneCountryCode || "+39",
                    });
                }
                setIsPhoneDataLoaded(true);
            } catch (error) {
                setIsPhoneDataLoaded(true);
            }
        };

        fetchTransactionData();
    }, [transactionId]);

    const sendSms = React.useCallback(async () => {
        if (!transactionId) {
            navigate("/error-page");
            return;
        }

        if (!isPhoneDataLoaded) {
            return;
        }

        if (!phoneData || !phoneData.phoneNumber) {
            navigate("/error-page");
            return;
        }

        // Prevent duplicate SMS requests
        if (smsRequestInProgressRef.current) {
            return;
        }

        try {
            smsRequestInProgressRef.current = true;
            const response = await sendSmsWithTan(
                transactionId,
                phoneData.phoneNumber,
                phoneData.phoneCountryCode
            );
            return response;
        } catch (error) {
            navigate("/error-page");
        } finally {
            smsRequestInProgressRef.current = false;
        }
    }, [transactionId, phoneData, isPhoneDataLoaded, navigate]);

    const validateTan = React.useCallback(async (tan: string) => {
        if (!transactionId) {
            navigate("/error-page");
            return;
        }

        setIsLoading(true);
        try {
            const response = await continueJourney(transactionId, tan);

            if (response?.message === "link-expired") {
                navigate("/error-page");
                return;
            }

            setShowTanAuthentication(false);

            const dealerNumber = searchParams.get("DealerCompanyNumber");

            // Construct the full URL with query params and hash route
            const queryString = dealerNumber
                ? `transactionId=${transactionId}&DealerCompanyNumber=${dealerNumber}`
                : `transactionId=${transactionId}`;
            
            // Use window.location to set both query params and hash
            window.location.href = `${window.location.origin}${window.location.pathname}?${queryString}#/match-maker/question-page1`;

            return { transaction: { status: response?.status || "success" } };
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [transactionId, navigate, searchParams, setShowTanAuthentication]);
    React.useEffect(() => {
        const localization = continueLaterLocalization({
            locale: "it",
            brandName: brand,
            dataProtectionLink: "https://www.vwfs.it/privacy-policy/vwl.html",
        });
        i18n.addResourceBundle("it", "sf-sac", localization, true, true);
    });
    return (
        <>
            {transactionId && showTanAuthentication && isPhoneDataLoaded && (
                <TanAuthentication
                    version={1}
                    transactionId={transactionId}
                    productName={productName}
                    visible
                    customSendRequestTan={sendSms}
                    customSendRequestData={validateTan}
                    onShow={() => { }}
                    onClickResendTan={() => { }}
                    onError={() => {
                        navigate("/error-page");
                    }}
                    externalLinkExpiryMessage={false}
                    onLinkExpired={() => {
                        navigate("/error-page");
                    }}
                    navigateToStart={() => {
                        navigate("/");
                    }}
                />
            )}
            {isLoading && <Spinner fullPage />}
        </>
    );
};
